/**
 * Turns the served section registry into MUI column definitions.
 *
 * Nothing here knows what a column *means* — the registry carries the key,
 * label, group and format, and this maps those onto `GridColDef`s. Adding a
 * stat to the roll-up therefore takes no frontend change.
 */

import {
  GridColDef,
  GridColumnGroupingModel,
} from "@mui/x-data-grid-premium";

import {
  PlayerStatColumn,
  PlayerStatFormat,
  PlayerStatSection,
  PlayerStatsPopulation,
} from "../../../models/PlayerStats";
import { activeFilters, ColumnFilter } from "./filters";
import { formatSigned } from "../../../util/formatSigned";

const EM_DASH = "—";

/** Rows carry sparse `v`/`p`/`t` maps keyed by column key. */
export interface StatRowData {
  v: Record<string, number>;
  p: Record<string, number>;
  t: Record<string, string>;
}

export function formatStat(
  value: number | string | null | undefined,
  format: PlayerStatFormat,
): string {
  if (value == null || value === "") return EM_DASH;
  if (format === "text") return String(value);
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return EM_DASH;

  switch (format) {
    // Signed impact reads better with an explicit + — the point is the sign. A value
    // that rounds to zero shows none (formatSigned, the reports' one signed format).
    case "pm":
      return formatSigned(n, 1);
    // Two decimals, because the values it carries (points per chance above or below
    // expectation) live in the hundredths — one decimal renders most of them as 0.0.
    case "pm2":
      return formatSigned(n, 2);
    // Same, but the roll-up stores these as 0-1 fractions.
    case "pmp":
      return formatSigned(n * 100, 1);
    case "pct":
      return `${n.toFixed(1)}%`;
    case "p3":
      return `${(n * 100).toFixed(1)}%`;
    case "r1":
      return n.toFixed(1);
    case "ppp":
    case "ratio":
      return n.toFixed(2);
    case "n0":
      return Math.round(n).toLocaleString("en-US");
    default:
      return String(n);
  }
}

/** Group headers are the registry's `group`, collapsed over runs of columns.
 *  MUI needs adjacent children, which the registry order already guarantees. */
export function buildColumnGroups(
  columns: PlayerStatColumn[],
): GridColumnGroupingModel {
  const groups: GridColumnGroupingModel = [];
  columns.forEach((column) => {
    const last = groups[groups.length - 1];
    // `groupBreak` starts a new visual run inside a same-named group (e.g. the
    // coverage scheme vs the BH-navigation pair) — the header text continues but
    // the group divider is drawn.
    if (last && last.headerName === column.group && !column.groupBreak) {
      last.children.push({ field: column.key });
    } else {
      groups.push({
        groupId: `${column.group}-${column.key}`,
        headerName: column.group,
        headerAlign: "center",
        children: [{ field: column.key }],
      });
    }
  });
  return groups;
}

/**
 * `renderCell` is supplied by the caller so the same column build serves the
 * league table and the per-player table, which differ only in how a cell knows
 * its percentile.
 */
/**
 * Applied to the last column of each group so the divider between, say, WAYD and
 * EPM reads heavier than the one between NET and OFF. Also marks the end of the
 * pinned identity block.
 *
 * A class rather than a `:last-of-type` rule: every cell in a row is a `div`, so
 * `:last-of-type` matches the last cell in the whole row regardless of the class
 * it's chained to.
 */
export const GROUP_END_CLASS = "player-stats-group-end";

export function buildStatColumns(
  columns: PlayerStatColumn[],
  renderCell: GridColDef["renderCell"],
): GridColDef[] {
  return columns.map((column, index) => {
    const endsGroup =
      index === columns.length - 1 ||
      columns[index + 1].group !== column.group ||
      Boolean(columns[index + 1].groupBreak);
    return {
      field: column.key,
      headerName: column.label,
      description: column.note
        ? `${column.group} — ${column.label}. ${column.note}`
        : `${column.group} — ${column.label}`,
      width: column.width ?? (column.format === "text" ? 120 : 92),
      type: column.format === "text" ? "string" : "number",
      headerAlign: column.format === "text" ? "left" : "right",
      align: column.format === "text" ? "left" : "right",
      sortable: true,
      cellClassName: endsGroup ? GROUP_END_CLASS : undefined,
      headerClassName: endsGroup ? GROUP_END_CLASS : undefined,
      // Sort on the raw number, never the formatted string — "+2.7" and "10.0"
      // would otherwise order lexically.
      valueGetter: (_value, row: StatRowData) =>
        column.format === "text" ? row.t?.[column.key] : row.v?.[column.key],
      renderCell,
    };
  });
}

export function sectionColumns(
  sections: PlayerStatSection[],
  sectionKey: string | null,
): PlayerStatColumn[] {
  const section =
    sections.find((s) => s.key === sectionKey) ?? sections[0] ?? null;
  return section?.columns ?? [];
}

/**
 * The columns to show for one variant selection.
 *
 * The registry ships every alternative — PPP, xPPP and their difference all arrive
 * in the payload — and exactly one renders. A column with no `variant` is not part
 * of any alternative set and always renders.
 *
 * Unknown variants fall back to showing nothing rather than everything, so a server
 * that grows a fourth alternative can't triple the table width on an old client.
 */
export function visibleColumns(
  columns: PlayerStatColumn[],
  variant: string,
): PlayerStatColumn[] {
  return columns.filter((c) => !c.variant || c.variant === variant);
}

/**
 * Percentiles for the rows currently on screen, replacing the published ones.
 *
 * Midrank, matching the roll-up's own `pctl` so switching modes changes the
 * population and nothing else. Columns are ranked independently and only over
 * rows that actually have the stat — a player with no post-ups shouldn't count
 * as the worst post scorer in the league.
 */
/**
 * Midrank percentile of `value` within an ascending array — ties take the middle
 * of the range they span. Mirrors the roll-up's `bsb.pctl`, so a chip computed
 * here and one published in the blob mean the same thing.
 *
 * Returns null below five samples, where a percentile is theatre, not information.
 */
export function percentileIn(
  sorted: number[],
  value: number,
  lowerIsBetter?: boolean,
): number | null {
  if (sorted.length < 5) return null;
  let below = 0;
  let equal = 0;
  sorted.forEach((v) => {
    if (v < value) below += 1;
    else if (v === value) equal += 1;
  });
  let fraction = (below + 0.5 * equal) / sorted.length;
  if (lowerIsBetter) fraction = 1 - fraction;
  return Math.round(fraction * 100);
}

/** Columns a percentile is meaningful for. Raw counts rank mostly by availability,
 *  so the registry marks them `rankable: false` and neither the blob nor the
 *  server-side fill produces one — this keeps the client from inventing one. */
function rankableColumns(columns: PlayerStatColumn[]): PlayerStatColumn[] {
  return columns.filter((c) => c.format !== "text" && c.rankable !== false);
}

export function sortedValues<T extends StatRowData>(
  rows: T[],
  key: string,
): number[] {
  return rows
    .map((row) => row.v?.[key])
    .filter((v): v is number => typeof v === "number")
    .sort((a, b) => a - b);
}

/**
 * Rank each row against a population, keyed by row id.
 *
 * `populationFor` returns the ascending values a row should be measured against.
 * The league table passes the same rows it's showing; the player page passes that
 * season's league population, which is why this is parameterised rather than
 * closing over `rows`.
 */
export function rankRows<T extends StatRowData>(
  rows: T[],
  columns: PlayerStatColumn[],
  getId: (row: T) => string,
  populationFor: (column: PlayerStatColumn, row: T) => number[] | undefined,
): Record<string, Record<string, number>> {
  const ranked = rankableColumns(columns);
  const byRow: Record<string, Record<string, number>> = {};
  rows.forEach((row) => {
    const percentiles: Record<string, number> = {};
    ranked.forEach((column) => {
      const value = row.v?.[column.key];
      if (typeof value !== "number") return;
      const values = populationFor(column, row);
      if (!values) return;
      const pctl = percentileIn(values, value, column.lowerIsBetter);
      if (pctl != null) percentiles[column.key] = pctl;
    });
    byRow[getId(row)] = percentiles;
  });
  return byRow;
}

export function computeViewPercentiles<T extends StatRowData>(
  rows: T[],
  columns: PlayerStatColumn[],
  getId: (row: T) => string,
): Record<string, Record<string, number>> {
  const perColumn: Record<string, number[]> = {};
  rankableColumns(columns).forEach((column) => {
    perColumn[column.key] = sortedValues(rows, column.key);
  });
  return rankRows(rows, columns, getId, (column) => perColumn[column.key]);
}

/**
 * Playtime floors the roll-up defines its percentile populations with
 * (`compute_nba_populations` / `compute_prospect_populations`). Mirrored here so
 * "vs current filters" with nothing applied reproduces the published "vs league"
 * number instead of ranking against everyone who ever took a possession.
 */
const POSS_FLOOR: Record<string, { season: number; career: number }> = {
  nba: { season: 300, career: 500 },
  prospect: { season: 200, career: 400 },
};

/** Indices of a population that clear the roll-up's floor, matching
 *  `service.py::_fill_percentiles`: take the published population size off the
 *  top by possessions when we have it, else fall back to the floor constant. */
function flooredIndices(
  population: PlayerStatsPopulation,
  garden: string,
): number[] {
  const all = population.poss.map((_p, i) => i);
  const n = population.populationN;
  if (n && n <= all.length) {
    return all
      .slice()
      .sort((a, b) => (population.poss[b] ?? 0) - (population.poss[a] ?? 0))
      .slice(0, n);
  }
  const floor = (POSS_FLOOR[garden] ?? POSS_FLOOR.nba).season;
  const cleared = all.filter((i) => (population.poss[i] ?? 0) >= floor);
  return cleared.length ? cleared : all;
}

/**
 * Ascending values per column for the slice of a population that survives the
 * population-defining filters — what a player's row is then ranked against.
 *
 * Percentile-basis filters rank against the *floored, unfiltered* population, not
 * the surviving one. Ranking against the survivors would be circular: the filter
 * would change the percentiles it is filtering on. This also matches the league
 * table, whose percentile filters read server-computed values from the full
 * population.
 */
export function populationValues(
  population: PlayerStatsPopulation,
  columns: PlayerStatColumn[],
  garden: string,
  filters: { position: string; columnFilters: ColumnFilter[] },
): Record<string, number[]> {
  const base = flooredIndices(population, garden);
  const ranked = rankableColumns(columns);

  const active = activeFilters(filters.columnFilters);
  const basePctl: Record<string, number[]> = {};
  active.forEach((filter) => {
    if (filter.basis !== "p" || basePctl[filter.field]) return;
    const values = population.v[filter.field];
    if (!values) return;
    basePctl[filter.field] = base
      .map((i) => values[i])
      .filter((v): v is number => typeof v === "number")
      .sort((a, b) => a - b);
  });

  const column = (field: string) => columns.find((c) => c.key === field);

  const surviving = base.filter((i) => {
    if (filters.position !== "ALL" && population.pos[i] !== filters.position) {
      return false;
    }
    return active.every((filter) => {
      if (filter.field === "gp") return compare(population.gp[i], filter);
      if (filter.field === "poss") return compare(population.poss[i], filter);
      const value = population.v[filter.field]?.[i];
      if (typeof value !== "number") return false;
      if (filter.basis === "v") return compare(value, filter);
      const sorted = basePctl[filter.field];
      if (!sorted) return false;
      return compare(
        percentileIn(sorted, value, column(filter.field)?.lowerIsBetter),
        filter,
      );
    });
  });

  const out: Record<string, number[]> = {};
  ranked.forEach((col) => {
    const values = population.v[col.key];
    if (!values) return;
    out[col.key] = surviving
      .map((i) => values[i])
      .filter((v): v is number => typeof v === "number")
      .sort((a, b) => a - b);
  });
  return out;
}

function compare(value: number | null | undefined, filter: ColumnFilter): boolean {
  // A player with no post-ups has no post efficiency — that isn't a zero, and it
  // shouldn't sneak past a "≤" filter as though it were. Same rule as
  // `applyFilters`.
  if (typeof value !== "number") return false;
  return filter.op === "gte" ? value >= filter.value : value <= filter.value;
}
